/* Technician and role endpoints. docs/07-identity-rbac.md I.2. */

import { request } from '../client';
import { isMockTransport } from '../config';
import type {
  Role,
  RoleAssignment,
  RoleAssignmentCreate,
  Technician,
  TechnicianInvite,
  TechnicianInviteResult,
  UserStatus,
  Uuid,
} from '../types';

export async function listTechnicians(): Promise<readonly Technician[]> {
  if (isMockTransport()) {
    const { mockListTechnicians } = await import('@/mocks/handlers');
    return mockListTechnicians();
  }
  return request<readonly Technician[]>('/technicians');
}

export async function listRoles(): Promise<readonly Role[]> {
  if (isMockTransport()) {
    const { mockListRoles } = await import('@/mocks/handlers');
    return mockListRoles();
  }
  return request<readonly Role[]>('/roles');
}

/*
  The three writes below all need technician.manage, which is [dangerous].

  The privilege escalation rule from docs/07-identity-rbac.md I.2 is worth restating at the
  call site, because it is the one an over-helpful UI would quietly break: a user may not
  grant a permission they do not themselves hold at that scope. The console computes the same
  answer locally so it can explain a refusal before the round trip and disable the confirm
  button, and that computation is an explanation, never the control. The authorization layer
  re-derives it from the database and a request that gets past the UI still gets a 403.
*/

export async function inviteTechnician(body: TechnicianInvite): Promise<TechnicianInviteResult> {
  if (isMockTransport()) {
    const { mockInviteTechnician } = await import('@/mocks/handlers');
    return mockInviteTechnician(body);
  }
  return request<TechnicianInviteResult>('/technicians', { method: 'POST', body });
}

export async function createRoleAssignment(
  body: RoleAssignmentCreate,
): Promise<RoleAssignment> {
  if (isMockTransport()) {
    const { mockCreateRoleAssignment } = await import('@/mocks/handlers');
    return mockCreateRoleAssignment(body);
  }
  return request<RoleAssignment>('/role-assignments', { method: 'POST', body });
}

export async function deleteRoleAssignment(assignmentId: Uuid): Promise<void> {
  if (isMockTransport()) {
    const { mockDeleteRoleAssignment } = await import('@/mocks/handlers');
    return mockDeleteRoleAssignment(assignmentId);
  }
  return request<void>(`/role-assignments/${assignmentId}`, { method: 'DELETE' });
}

/**
 * Suspend, re-enable or disable a technician. Separate from the invite call because it is a
 * different permission decision in the audit record and because the body is one field.
 */
export async function setTechnicianStatus(
  userId: Uuid,
  status: UserStatus,
): Promise<Technician> {
  if (isMockTransport()) {
    const { mockSetTechnicianStatus } = await import('@/mocks/handlers');
    return mockSetTechnicianStatus(userId, status);
  }
  return request<Technician>(`/technicians/${userId}/status`, {
    method: 'PATCH',
    body: { status },
  });
}
